import { useNavigate } from "react-router-dom";
import { FormEvent, Fragment, useEffect, useState } from "react";

import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import Container from "@mui/material/Container";
import TextField from "@mui/material/TextField";
import BadgeIcon from "@mui/icons-material/Badge";
import { Microsoft } from "@mui/icons-material";

import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import VisibilityOffOutlinedIcon from "@mui/icons-material/VisibilityOffOutlined";
import {
  Button,
  ButtonBase,
  Divider,
  IconButton,
  InputAdornment,
  Stack,
} from "@mui/material";

import LOGO from "../../semiauth.logo.png";
import { useAuth } from "./AuthProvider";
import { useVaultLocalSignin, useVaultSignin } from "../../hooks/vault/base";

const SigninLocal = () => {
  // const darkMode = useMediaQuery("(prefers-color-scheme: dark)");

  const navigate = useNavigate();

  const { auth, user } = useAuth();
  const [email, setEmail] = useState(user.email);

  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const toggleShowPassword = () => setShowPassword(!showPassword);

  const sso = useVaultSignin();
  const signin = useVaultLocalSignin();

  const showPing = user.idp.includes("PING");
  const showOkta = user.idp.includes("OKTA");
  const showAuth0 = user.idp.includes("AUTH0");
  const showLocal = user.idp.includes("LOCAL");
  const showMicrosoft = user.idp.includes("MICROSOFT");

  const handleLogin = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    // attempt signin
    signin.mutate({ email, password });
  };

  useEffect(() => {
    if (auth) navigate("/connect");
  }, [auth]);

  // if user email not found, redirect to welcome page
  useEffect(() => {
    if (!user.email) navigate("/welcome");
  }, [user]);

  useEffect(() => {
    document.title = "[semiauth] | signin";
  }, []); // run once on mount

  return (
    <Container
      maxWidth="xs"
      component="main"
      sx={{
        display: "flex",
        textAlign: "center",
        alignItems: "center",
        marginBlock: "auto",
        flexDirection: "column",
        justifyContent: "center",
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          flexDirection: "column",
        }}
      >
        <img src={LOGO} alt="[semiauth] logo" className="logo" />

        <Box component="form" onSubmit={handleLogin} noValidate>
          {showLocal && (
            <Fragment>
              <TextField
                margin="normal"
                required
                disabled
                fullWidth
                id="email"
                name="email"
                label="Email"
                value={email}
                autoComplete="email"
                autoFocus
              />

              <TextField
                margin="normal"
                required
                fullWidth
                id="password"
                name="password"
                label="Password"
                type={showPassword ? "text" : "password"}
                onChange={(e: any) => setPassword(e.target.value)}
                autoComplete="current-password"
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        edge="end"
                        size="small"
                        onClick={toggleShowPassword}
                        aria-label="toggle password visibility"
                      >
                        {showPassword ? (
                          <VisibilityOffOutlinedIcon />
                        ) : (
                          <VisibilityOutlinedIcon />
                        )}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />

              <Button
                fullWidth
                type="submit"
                variant="contained"
                disabled={!(email && password)}
                sx={{ mt: 2 }}
              >
                Sign In
              </Button>

              <Grid
                container
                alignItems="center"
                justifyContent="space-between"
                mt={1}
              >
                <Grid item xs textAlign="left">
                  <ButtonBase onClick={() => navigate("/forgot-password")}>
                    Forgot password
                  </ButtonBase>
                </Grid>
              </Grid>

              <Divider sx={{ mt: 2 }}>or</Divider>
            </Fragment>
          )}

          <Stack spacing={2} marginTop={2}>
            {showMicrosoft && (
              <Button
                fullWidth
                variant="contained"
                onClick={() => sso.mutate({ email, type: "MICROSOFT" })}
                endIcon={<Microsoft color="inherit" />}
              >
                Sign in with Microsoft
              </Button>
            )}

            <Button
              disabled
              fullWidth
              variant="contained"
              onClick={() => sso.mutate({ email, type: "PING" })}
              endIcon={<BadgeIcon color="inherit" />}
            >
              Verify with ID.ME
            </Button>
          </Stack>
        </Box>
      </Box>
    </Container>
  );
};

export default SigninLocal;
